import { Pencil, X } from 'lucide-react'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'

interface Props {
  open: boolean
  onClose: () => void
  onEdit: () => void
  items: Array<Record<string, unknown>> | null
  lessonTitle?: string
}

function asString(v: unknown): string | null {
  if (v == null) return null
  const s = String(v).trim()
  return s.length ? s : null
}

function asNumber(v: unknown): number | null {
  if (typeof v === 'number') return v
  if (typeof v === 'string' && /^\d+$/.test(v)) return Number(v)
  return null
}

export function EssentialCheckDetailModal({ open, onClose, onEdit, items, lessonTitle }: Props) {
  if (!open) return null
  const rows = items ?? []
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              필수 체크 상세 {lessonTitle && <span className="text-slate-500 font-normal">— {lessonTitle}</span>}
            </h2>
            <p className="text-xs text-slate-500">{rows.length}개 항목</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded hover:bg-teal-100"
            >
              <Pencil className="w-3 h-3" />
              수정
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-5">
          {rows.length === 0 ? (
            <div className="py-10 text-sm text-center text-slate-400">데이터가 없습니다</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs font-medium tracking-wide text-left uppercase bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-12">#</th>
                  <th className="px-3 py-2 w-40">정답/라벨</th>
                  <th className="px-3 py-2">해설</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row, i) => {
                  const num = asNumber(row.number) ?? i + 1
                  return (
                    <tr key={i} className="align-top">
                      <td className="px-3 py-2 text-xs font-mono text-teal-600">{num}</td>
                      <td className="px-3 py-2 text-slate-700">
                        <LearningContentText text={asString(row.answer) ?? '-'} />
                      </td>
                      <td className="px-3 py-2 text-slate-700 whitespace-pre-wrap">
                        <LearningContentText text={asString(row.desc) ?? '-'} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
